// row-state.ts — a PURE, keyed, TTL-aware store for TRANSIENT per-row UI
// state (design-system-foundation; built ONCE because framework-column needs
// it for 'updating'/'failed' and abort-flow needs it for 'aborting').
//
// WHY IT EXISTS: AG Grid's refreshCells destroys renderer DOM, so any
// transient state a cell renderer keeps on its own element ("this row is
// aborting", "this update failed 20s ago") dies on the next repaint. The
// state must live OUTSIDE the grid; renderers re-READ it at render time and
// the store's notifications tell the grid which rows to repaint.
//
// SHAPE: a pure reducer over an immutable Map (exported, so tests drive
// transitions directly and the framework's instruments can replay them),
// plus a thin stateful wrapper (createRowStore) that owns subscription and
// TTL scheduling. The reducer NEVER reads the clock — time arrives in the
// action, which is what makes expiry testable at the boundary (at+ttl-1 vs
// at+ttl) instead of "eventually".
//
// TTL: an entry may carry ttlMs (e.g. framework-column's 'failed' auto-clear,
// R18). Reads are TTL-aware — an expired entry answers null even before a
// sweep physically removes it — so a renderer can never paint a state that
// has already lapsed, no matter when the sweep timer fires.

export interface RowStateEntry<S extends string = string> {
  state: S;
  /** Epoch ms when the entry was set (the TTL anchor). */
  at: number;
  /** Lifetime in ms, or null = lives until cleared/replaced. */
  ttlMs: number | null;
  /** Consumer payload (e.g. the failure detail behind click-for-details). */
  data?: unknown;
}

export type RowStateMap<S extends string = string> = ReadonlyMap<string, RowStateEntry<S>>;

export type RowStateAction<S extends string = string> =
  | { type: 'set'; key: string; state: S; at: number; ttlMs?: number | null; data?: unknown }
  | { type: 'clear'; key: string }
  | { type: 'sweep'; now: number };

export function emptyRowState<S extends string = string>(): RowStateMap<S> {
  return new Map();
}

/** When the entry lapses, or null for entries that live until cleared. */
export function expiresAt(e: RowStateEntry): number | null {
  return e.ttlMs === null ? null : e.at + e.ttlMs;
}

function isLive(e: RowStateEntry, now: number): boolean {
  const exp = expiresAt(e);
  return exp === null || now < exp;
}

/** TTL-aware read: an expired entry answers null even before a sweep. */
export function rowState<S extends string>(map: RowStateMap<S>, key: string, now: number): S | null {
  const e = map.get(key);
  return e && isLive(e, now) ? e.state : null;
}

/** TTL-aware entry read, for consumers that need the payload (`data`). */
export function rowEntry<S extends string>(
  map: RowStateMap<S>,
  key: string,
  now: number,
): RowStateEntry<S> | null {
  const e = map.get(key);
  return e && isLive(e, now) ? e : null;
}

/** The earliest pending expiry across the map, or null (drives scheduling). */
export function nextExpiry(map: RowStateMap): number | null {
  let min: number | null = null;
  for (const e of map.values()) {
    const exp = expiresAt(e);
    if (exp !== null && (min === null || exp < min)) min = exp;
  }
  return min;
}

/** The reducer. Returns the SAME reference when nothing changed (a sweep
 * with nothing expired, a clear of an absent key) so subscribers can compare
 * by identity. */
export function rowStateReducer<S extends string>(
  map: RowStateMap<S>,
  action: RowStateAction<S>,
): RowStateMap<S> {
  switch (action.type) {
    case 'set': {
      const next = new Map(map);
      next.set(action.key, {
        state: action.state,
        at: action.at,
        ttlMs: action.ttlMs ?? null,
        data: action.data,
      });
      return next;
    }
    case 'clear': {
      if (!map.has(action.key)) return map;
      const next = new Map(map);
      next.delete(action.key);
      return next;
    }
    case 'sweep': {
      let next: Map<string, RowStateEntry<S>> | null = null;
      for (const [k, e] of map) {
        if (!isLive(e, action.now)) {
          if (!next) next = new Map(map);
          next.delete(k);
        }
      }
      return next ?? map;
    }
  }
}

// ---------------------------------------------------------------------
// The stateful wrapper — subscription + TTL scheduling over the reducer.
// ---------------------------------------------------------------------

export interface RowStore<S extends string = string> {
  /** TTL-aware state read (renderers call this per render). */
  get(key: string): S | null;
  /** TTL-aware full-entry read (payload access). */
  entry(key: string): RowStateEntry<S> | null;
  set(key: string, state: S, opts?: { ttlMs?: number | null; data?: unknown }): void;
  clear(key: string): void;
  /** Fires with the CHANGED keys — exactly what a grid consumer should
   * refreshCells for. Expiry fires it too (that is R18's auto-clear repaint). */
  subscribe(fn: (changedKeys: readonly string[]) => void): () => void;
  snapshot(): RowStateMap<S>;
}

export interface RowStoreDeps {
  /** Injectable clock (tests pin it; default Date.now). */
  now?: () => number;
  /** Injectable timer: schedule fn after ms, return a cancel. Default
   * setTimeout/clearTimeout. */
  schedule?: (fn: () => void, ms: number) => () => void;
}

export function createRowStore<S extends string = string>(deps: RowStoreDeps = {}): RowStore<S> {
  const now = deps.now ?? (() => Date.now());
  const schedule =
    deps.schedule ??
    ((fn: () => void, ms: number): (() => void) => {
      const t = setTimeout(fn, ms);
      return () => clearTimeout(t);
    });

  let map: RowStateMap<S> = emptyRowState<S>();
  const subs = new Set<(changedKeys: readonly string[]) => void>();
  let cancelTimer: (() => void) | null = null;

  const notify = (keys: readonly string[]): void => {
    if (keys.length === 0) return;
    for (const fn of subs) {
      try {
        fn(keys);
      } catch {
        /* one bad subscriber must not starve the rest */
      }
    }
  };

  /** (Re)arm the sweep timer at the earliest pending expiry. */
  const arm = (): void => {
    cancelTimer?.();
    cancelTimer = null;
    const exp = nextExpiry(map);
    if (exp === null) return;
    cancelTimer = schedule(sweep, Math.max(0, exp - now()));
  };

  const sweep = (): void => {
    const t = now();
    const before = map;
    map = rowStateReducer(map, { type: 'sweep', now: t });
    if (map !== before) {
      const gone: string[] = [];
      for (const k of before.keys()) if (!map.has(k)) gone.push(k);
      notify(gone);
    }
    arm();
  };

  return {
    get: (key) => rowState(map, key, now()),
    entry: (key) => rowEntry(map, key, now()),
    set: (key, state, opts) => {
      map = rowStateReducer(map, {
        type: 'set',
        key,
        state,
        at: now(),
        ttlMs: opts?.ttlMs ?? null,
        data: opts?.data,
      });
      notify([key]);
      arm();
    },
    clear: (key) => {
      const before = map;
      map = rowStateReducer(map, { type: 'clear', key });
      if (map !== before) notify([key]);
      arm();
    },
    subscribe: (fn) => {
      subs.add(fn);
      return () => subs.delete(fn);
    },
    snapshot: () => map,
  };
}
